import React, { useEffect, useState } from 'react';
import { Route } from 'react-router-dom';
import { LinearProgress, Icon } from '@mui/material';
import { NavBarContent } from 'feature/nav/NavBar';
import DocumentTabs from '../DocumentTabs';
import { StyledContent } from './Document.styles';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { useShowChanges } from 'global/hooks/useShowChanges';
import { first, get } from 'global/lib/lodash';
import { Transform, Includes } from '../DocumentList';
import DiffViewer from 'global/components/diff-text';
import withDocumentParent from './withDocumentParent';
import { Button } from 'global/components/Button';
import { useHashView } from 'feature/share/useHashView';
import ShareLinkModal from 'feature/share/ShareLinkModal';
import SnackAlerts from 'global/components/snack-alerts';

export default function Document(props) {
  const { match } = props;
  const { showChangesOn } = useShowChanges();

  const edition_id = get(props, 'editionId', get(match, 'params.library'));
  const classification_id = get(props, 'classificationId', get(match, 'params.document'));
  // PFM-2641: /sharehash/v1/editions/:id 500s, but the sharehash whitelist allows
  // include=edition on the edition-documents show route (scope.id is the shared
  // edition-document id), so a hashed viewer reads the edition through that instead.
  // scorecards.checklists isn't whitelisted and is only needed for edit flows,
  // which a hashed viewer never reaches.
  const { active: hashViewActive, scope: hashScope } = useHashView();
  const useHashEditionRead = hashViewActive && !!get(hashScope, 'id');
  // PFM-2689: in the Account Settings > Content "Add Existing" picker a course /
  // shared source can have no real edition id (edition_id resolves to undefined),
  // which made the normal read hit `/editions/undefined` (404 + CRA error overlay).
  // Gate the read on a usable id for the active path (no-op for normal/hash views).
  const edition = useRetrieveData(
    { url: useHashEditionRead ? `/edition-documents/${hashScope.id}` : `/editions/${edition_id}` },
    {
      build: ['editions'],
      waitFor: useHashEditionRead || !!edition_id,
      params: { include: useHashEditionRead ? 'edition' : 'scorecards,scorecards.checklists' },
      transformData: (d) => first(d),
    }
  );

  const { data, loading, update } = useRetrieveData(
    { url: `/edition-documents` },
    {
      useLangFilter: true,
      build: ['editionDocuments'],
      params: {
        include: 'document',
        filter: { classification_id, edition_id },
        editMode: 1,
      },
      useEffect: true,
      waitFor: classification_id,
      transformData: Transform,
    }
  );
  
  return <_Document {...props} doc={data} {...{data, edition, edition_id, classification_id, showChangesOn, loading, update }} />
}

const _Document = withDocumentParent((props) => {
  const { data, edition_id, classification_id, edition, editionParent, showChangesOn, parentDocument, documentParentId, loading, update } = props;
  const { active: hashViewActive } = useHashView();
  const [shareModalOpen, setShareModalOpen] = useState(false);
  // PFM-2648: confirmation toast after a Share email is sent (the modal closes
  // on send, so the toast is owned here, not inside ShareLinkModal).
  const [shareSentAlert, setShareSentAlert] = useState();

  const showChanges = showChangesOn && !!documentParentId && !parentDocument.loading;
  const diffElement = showChanges ? <DiffViewer oldText={get(parentDocument, 'data.name')} newText={get(data, '0.document.name')} diffType="words" /> : get(data, '0.document.name');

  // PFM-2564: document is identified by edition_id + classification (route /library/:library/document/:document).
  // The hash redemption uses the edition-document id; we approximate with first(data) which carries the joined id.
  const editionDocumentId = get(data, '0.id');
  const documentName = get(data, '0.document.name');

  const shareActions = !hashViewActive && editionDocumentId ? (
    <Button
      data-testid="document-share-btn"
      onClick={() => setShareModalOpen(true)}
      startIcon={<Icon>ios_share</Icon>}
    >
      Share
    </Button>
  ) : null;

  return (
    <StyledContent fullWidth={hashViewActive}>
      <NavBarContent
        pageTitle={loading ? 'Loading...' : diffElement}
        link={'library/' + edition_id}
        closeProps={props.closeProps}
        actions={shareActions}
      />
      {shareModalOpen && (
        <ShareLinkModal
          open={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          onSent={({ message }) => setShareSentAlert({ message })}
          scope={{
            kind: 'document',
            id: editionDocumentId,
            editionId: edition_id,
            classificationId: classification_id || get(data, '0.document.classification.id'),
          }}
          title={documentName || 'document'}
        />
      )}
      <SnackAlerts alerts={shareSentAlert} setAlerts={setShareSentAlert} />
      <Route
        exact
        path={`/library/:library/document/:document`}
        render={(p) => (
          <DocumentTabs
            {...p}
            edition={edition.data}
            editionParent={editionParent.data}
            documents={data}
            parentDocument={parentDocument}
            documentParentId={documentParentId}
            update={update}
            userCanEdit={get(edition, 'data.userCanEdit', false)}
          />
        )}
      />
      {props.isModal && (
        <DocumentTabs
          {...props}
          documents={data}
          edition={edition.data}
          editionParent={editionParent.data}
          update={update}
          canEdit={get(edition, 'data.userCanEdit', false)}
        />
      )}
      {loading && <LinearProgress />}
    </StyledContent>
  );
});
